"use client";

import { memo, useCallback, useEffect, useRef, useState } from "react";
import type { CarouselProps } from "./types";
import "keen-slider/keen-slider.min.css";
import { KeenSliderInstance } from "keen-slider/react";
import { useSliderEdges } from "./hooks/useCarouselEdges";
import { useHorizontalWheelNavigation } from "./hooks/useHorizontalWheelNavigation";
import { CarouselNavigationButton } from "./CarouselNavigationButton";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useSyncSlideClasses } from "./hooks/useSyncSlideClasses";
import { useAsyncKeenSlider } from "./hooks/useAsyncKeenSlider";

const CarouselMobile: React.FC<CarouselProps> = ({
  className,
  children,
  overflowVisible = false,
  showNavigation = false,
  showPagination = false,
  navigationSlot,
  getSlider,
  options = {},
  ...rest
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<KeenSliderInstance | null>(null);
  const notMobile = useMediaQuery("(min-width: 768px)");
  const KeenSlider = useAsyncKeenSlider();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderReady, setSliderReady] = useState<KeenSliderInstance | null>(
    null
  );

  const { isBeginning, isEnd, canScroll, updateEdges } =
    useSliderEdges(sliderReady);
  const { syncSlideClasses } = useSyncSlideClasses();

  useHorizontalWheelNavigation({
    slider: sliderReady,
    container: sliderReady?.container,
  });

  useEffect(() => {
    const container = sliderRef.current;

    if (!KeenSlider) {
        return
    }

    if (!container || notMobile) {
      instanceRef.current?.destroy();
      instanceRef.current = null;
      setSliderReady(null);
      return;
    }

    instanceRef.current?.destroy();

    const slider = new KeenSlider(container, {
      loop: false,
      slides: { perView: "auto" },
      selector: "[data-carousel-slide]",
      created: (slider) => {
        updateEdges(slider);
        setSliderReady(slider);
        getSlider?.(slider);
      },
      slideChanged: (slider) => {
        updateEdges(slider);
        setCurrentSlide(slider.track.details.rel);
      },
      detailsChanged: (slider) => {
        syncSlideClasses(slider);
      },
      ...options,
    });

    instanceRef.current = slider;
    

    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
      setSliderReady(null);
    };
  }, [notMobile, updateEdges, options, KeenSlider]);

  const goToSlide = useCallback(
    (idx: number) => {
      instanceRef.current?.moveToIdx(idx);
    },
    [instanceRef]
  );

  const goToPrevSlide = useCallback(() => {
    instanceRef.current?.prev();
  }, [instanceRef]);

  const goToNextSlide = useCallback(() => {
    instanceRef.current?.next();
  }, [instanceRef]);

  return (
    <div className="grid grid-cols-1 place-content-start">
      <div
        ref={sliderRef}
        className={cn(
          "keen-slider flex md:overflow-visible md:[display:revert]",
          { "overflow-visible": overflowVisible },
          className
        )}
        {...rest}
      >
        {children}
      </div>
      {sliderReady && showPagination && (
        <div className="flex items-center justify-center gap-6 mt-6 py-4">
          {sliderReady?.track?.details?.slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={cn(
                "h-5 w-5 bg-neutral-300 rounded-full cursor-pointer relative outline-none inline-flex select-none shrink-0 transition-colors disabled:cursor-not-allowed",
                {
                  "!bg-blue-500": currentSlide === idx,
                }
              )}
            />
          ))}
        </div>
      )}
      {showNavigation && (
        <div className="mt-6 flex items-center gap-6 md:hidden">
          {canScroll && (
            <>
              <CarouselNavigationButton
                onClick={goToPrevSlide}
                reverse
                disabled={isBeginning}
                aria-disabled={isBeginning}
              />
              <CarouselNavigationButton
                onClick={goToNextSlide}
                disabled={isEnd}
                aria-disabled={isEnd}
              />
            </>
          )}
          {navigationSlot}
        </div>
      )}
    </div>
  );
};

export default memo(CarouselMobile);
